import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Scan, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function TicketScanner() {
  const { token } = useAuth();
  const [code, setCode] = useState("");
  const [eventId, setEventId] = useState("");
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);

  const handleScan = async () => {
    if (!code.trim()) {
      toast.error("Enter a ticket code");
      return;
    }
    setScanning(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/tickets/validate`,
        { ticket_code: code.trim(), event_id: eventId.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data);
      if (res.data.valid) {
        toast.success(`Admitted — ${res.data.holder_name}`);
        setCode("");
      } else {
        toast.error(res.data.reason || "Invalid ticket");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050810] text-white pb-20" data-testid="ticket-scanner-page">
      <header className="sticky top-0 z-30 glass-strong border-b border-[#1A2030]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Link to="/dashboard" className="text-[#8A92A0] hover:text-white" data-testid="scanner-back">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <Scan className="w-6 h-6 text-[#EC008C]" />
          <h1 className="font-['Unbounded'] text-xl font-bold">Ticket Scanner</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 pt-6">
        <div className="card p-6">
          <p className="text-[#8A92A0] text-sm mb-4">
            Validate a fan&apos;s ticket at the door. Paste the code from their QR or e-ticket — duplicates and counterfeits are auto-rejected (HMAC-signed, single-use).
          </p>

          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#8A92A0]">Ticket code *</label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="SF-XXXXXXXX-XXXXXX"
                className="bg-[#0A0E18] border-[#1A2030] text-white text-lg font-mono"
                data-testid="scanner-code-input"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#8A92A0]">Event ID (optional)</label>
              <Input
                value={eventId}
                onChange={(e) => setEventId(e.target.value)}
                placeholder="event_..."
                className="bg-[#0A0E18] border-[#1A2030] text-white text-sm font-mono"
                data-testid="scanner-event-input"
              />
            </div>
            <Button
              onClick={handleScan}
              disabled={scanning || !code.trim()}
              className="w-full bg-[#EC008C] text-black hover:bg-[#FF3399]"
              data-testid="scanner-validate-btn"
            >
              {scanning ? "Verifying…" : "Validate ticket"}
            </Button>
          </div>

          {result && (
            <div className={`mt-4 p-4 rounded-xl border ${
              result.valid
                ? "bg-emerald-500/10 border-emerald-500/40"
                : "bg-red-500/10 border-red-500/40"
            }`} data-testid="scanner-result">
              <div className="flex items-start gap-3">
                {result.valid ? (
                  <CheckCircle2 className="w-6 h-6 text-emerald-400 flex-shrink-0" />
                ) : result.reason === "Already scanned" ? (
                  <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <p className={`font-bold text-lg ${result.valid ? "text-emerald-400" : "text-red-400"}`}>
                    {result.valid ? "✓ ADMITTED" : `✗ ${(result.reason || "Invalid").toUpperCase()}`}
                  </p>
                  {result.valid && (
                    <>
                      <p className="text-white mt-1" data-testid="scanner-holder">{result.holder_name}</p>
                      <p className="text-[#8A92A0] text-sm">{result.event_title}</p>
                      <p className="text-[#6A7280] text-xs mt-1">Scanned at {new Date(result.scanned_at).toLocaleString()}</p>
                    </>
                  )}
                  {!result.valid && result.scanned_at && (
                    <p className="text-yellow-400 text-xs mt-1">Previously scanned at {new Date(result.scanned_at).toLocaleString()}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
